/* eslint-disable react/boolean-prop-naming */
/* eslint-disable react/jsx-handler-names */
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import CustomImage from "../../../atoms/customImage";
import TextField from "../../../atoms/textField";
import { constants, toastStyles } from "../../../../constants/pageConstants/pdpConstants";
import Typography from "../../../atoms/typography";
import Drawer from "../../drawer";
import ButtonComponent from "../../../atoms/button";
import TextPersonalization from "../textPersonalization";
import IconButtonComponent from "../../../atoms/iconButton";
import Toast from "../../../atoms/toast";
import {
  setMultiImageText,
  setSuccessfullyAddedToast,
  setUserPersonalization,
} from "../../../../store/slices/personalizationSlice";
import { checkPersonalizationValidation, handleSetUserPersonalizations } from "../../../../utils/pdp/personalization";
import { removeLocalStorageItem } from "../../../../utils/storage/localStorageUtil";
import { unlockBody } from "../../../../utils/document";
import { pageName } from "../../../../utils/common";
import useAnalytics from "../../../../analytics/useAnalytics";
import { cleverTapCommonEvents, validationFor } from "../../../../analytics/clevertapConstants";

/**
 * MultiTextPersonlization component for managing and rendering personalized text input fields.
 *
 * @param {boolean} [props.isMobile=true] - Flag indicating if the component is in mobile view.
 * @param {string} props.productId - The ID of the product being personalized.
 * @param {Array} props.personalizedOptions - An array of options for personalization, containing field details.
 * @param {boolean} props.imageWithText - Determines if the personalization involves both image and text.
 * @param {boolean} props.isUploadIncomplete - Flag indicating if the upload process is incomplete.
 * @param {Function} props.setTextEntered - Callback function to set the state of whether text has been entered.
 * @param {Function} props.setMultiImageDrawer - Callback function to toggle the state of the multi-image drawer.
 * @param {Function} props.setPreviewImage - Callback function to set the preview image.
 *
 * @returns {JSX.Element} The rendered MultiTextPersonlization component.
 */
const MultiTextPersonlization = ({
  isMobile,
  productId,
  personalizedOptions,
  imageWithText,
  isUploadIncomplete,
  setTextEntered,
  setMultiImageDrawer,
  setPreviewImage,
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [multiTextDrawer, setMultiTextDrawer] = useState(false);
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [textsAddedSuccessfully, setTextsAddedSuccessfully] = useState(false);
  const [invalidFields, setInvalidFields] = useState({});
  const [multiTextPersonalization, setMultiTextPersonalization] = useState([]);
  const pathname = usePathname();

  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");

  const { product, multiImageConfig, userPersonalization, personalizationError } = useSelector(
    (state) => ({
      product: state?.pdp?.clientCurrentClickedItem,
      multiImageConfig: state?.personalization.multiImageConfig,
      userPersonalization: state.personalization.userPersonalization,
      personalizationError: state.pdp.personalizationError,
    }),
    shallowEqual,
  );
  const urlIdentifier = product?.urlIdentifier;

  useEffect(() => {
    dispatch(setMultiImageText(multiTextPersonalization));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiTextPersonalization]);

  useEffect(() => {
    if (personalizationError) {
      const cleverTapData = {
        page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
        pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
        validation_text: constants.THIS_IS_REQUIRED,
        validation_for: validationFor.NAME_TO_PRINT,
      };

      trackEvent(cleverTapCommonEvents.validationViewed, cleverTapData, ["clevertap"]);
    }
  }, [personalizationError]);

  /**
   * Handles input changes for personalized text fields.
   *
   * @param {string} value - The new value of the input field.
   * @param {number} index - The index of the input field being updated.
   * @param {string} fieldName - The name of the field being updated.
   */
  const onInputChange = (value, index, fieldName) => {
    // Reset invalid state for the field
    setInvalidFields((prev) => ({ ...prev, [fieldName]: false }));

    const newPersonalization = {
      fieldName: personalizedOptions[index].fieldName,
      contentType: personalizedOptions[index].contentType,
      index,
      label: personalizedOptions[index].label,
      value: [value],
    };
    setMultiTextPersonalization((prev) => {
      const updated = [...prev];
      updated[index] = newPersonalization;
      return updated;
    });
  };

  /**
   * Handles the closing of the drawer and saves or removes the personalization data.
   */
  const handleDrawerClose = () => {
    const isPersonalizationComplete = checkPersonalizationValidation(
      { personalizedOptions, personalizedProduct: true },
      multiTextPersonalization,
    );

    if (isPersonalizationComplete && !imageWithText) {
      setTextsAddedSuccessfully(true);
      handleSetUserPersonalizations(productId, multiTextPersonalization, userPersonalization, dispatch);
    } else {
      removeLocalStorageItem(productId);
      dispatch(setUserPersonalization(multiTextPersonalization));
      setTextsAddedSuccessfully(false);
    }

    // Close the drawer and reset invalid fields
    setMultiTextDrawer(false);
    setInvalidFields({});
  };

  /**
   * Validates inputs and dispatches personalization data if valid.
   */
  const onContinueMultiText = () => {
    const newInvalidFields = {};
    const personalizedImageDetails = {
      fieldName: "PersonalizedImage",
      contentType: "IMAGE",
      index: null,
      label: "Upload Personalized Image",
      value: multiImageConfig?.map((image) => image?.imgUrl),
    };
    personalizedOptions.forEach(({ fieldName }) => {
      const isFilled = multiTextPersonalization?.some(
        ({ fieldName: userField, value }) => userField === fieldName && value?.[0],
      );
      newInvalidFields[fieldName] = !isFilled;
    });

    setInvalidFields(newInvalidFields);
    if (Object.values(newInvalidFields).every((isInvalid) => !isInvalid)) {
      if (imageWithText) {
        if (isUploadIncomplete) {
          const tab = document.getElementById("tab_1");
          if (tab) {
            tab.click();
          }
        } else {
          setTextEntered(true);
          setPreviewImage(true);
          handleSetUserPersonalizations(
            productId,
            [personalizedImageDetails, ...multiTextPersonalization],
            userPersonalization,
            dispatch,
          );
          setMultiImageDrawer(false);
          const tab = document.getElementById("tab_1");
          if (tab) {
            tab.click();
          }
          dispatch(setSuccessfullyAddedToast(true));
        }
      } else {
        setMultiTextDrawer(false);
        setTextsAddedSuccessfully(true);
        handleSetUserPersonalizations(productId, multiTextPersonalization, userPersonalization, dispatch);
      }
    } else {
      setIsToastVisible(true);
    }
    unlockBody();
  };

  /**
   * Handles the click event for closing the multi-text drawer.
   * It resets the invalid fields, unlocks the body scroll, and closes the drawer.
   */
  const handleCloseMobileMultiTextDrawer = () => {
    setMultiTextDrawer(false);
    setInvalidFields({});
    unlockBody();
  };

  return imageWithText ? (
    <>
      <div className="p-20">
        {personalizedOptions?.map((option, index) => (
          <TextPersonalization
            productId={productId}
            key={index}
            indexNumber={index}
            option={option}
            multiText={true}
            isInvalid={invalidFields[option.fieldName]}
            onInputChange={(value) => onInputChange(value, index, option?.fieldName)}
            setTextsAddedSuccessfully={setTextsAddedSuccessfully}
            imageWithText={imageWithText}
            setMultiTextPersonalization={setMultiTextPersonalization}
          />
        ))}
      </div>

      <div className="bg-white fixed bottom-0 flex w-full flex-col items-center gap-10 border-t border-gray-200 px-[20px] py-[16px]">
        <ButtonComponent
          classes="text-base flex justify-center w-[360px]"
          buttonVariant="filled"
          label={imageWithText ? constants.CONTINUE : constants.SAVE_AND_CONTINUE}
          onClick={onContinueMultiText}
          isRemoveExtraPadding={true}
        />
      </div>

      <div className={`bottom-100 ${toastStyles}`}>
        <Toast
          variant="info"
          isToast={isToastVisible}
          header={constants.CONTINUE_WITH_DETAILS}
          duration={3000}
          onToastChange={() => setIsToastVisible(false)}
        />
      </div>
    </>
  ) : (
    <div>
      <Typography Tag="p" classes="w-full font-600 text-14 text-fnp-500 pb-3 lg:pb-[6px]">
        {constants.PERSONALIZED_HEADING}
      </Typography>
      <div className="relative">
        <TextField
          variant="primary"
          id="multi_text_personlizations"
          type="button"
          classes="text-grey-500 font-inter text-14 font-normal capitalize text-left cursor-pointer"
          inputValue={constants.CUSTOMIZE_PRODUCT}
          onClick={() => setMultiTextDrawer(true)}
          startAdornment={
            <CustomImage src="/icons/message-text.svg" width={24} height={24} alt="start icon" sizes="10vw" />
          }
          endAdornment={
            <CustomImage src="/icons/arrow-right-dark.svg" width={24} height={24} alt="arrow icon" sizes="10vw" />
          }
          isRequired={false}
          isError={personalizationError}
          message={personalizationError ? constants.THIS_IS_REQUIRED : ""}
          messageClass="w-full md:w-[254px] pe-16"
        />
        {textsAddedSuccessfully && (
          <CustomImage
            src="/icons/success.svg"
            width={17}
            height={17}
            className="absolute left-[185px] top-[16px]"
            alt="success_icon"
          />
        )}
      </div>
      <Drawer
        position={isMobile ? "bottom" : "right"}
        isOpen={multiTextDrawer}
        onDrawerClose={handleDrawerClose}
        styles={`p-0 h-full ${isMobile ? "" : "min-w-[360px] max-w-[400px] xll:max-w-[532px] xl:max-w-[400px]"}`}
        isDrawerCustomStyles={!isMobile}
      >
        <div
          className={`flex items-center justify-start ${isMobile ? "px-[16px] py-[10px] shadow sm:flex" : "bg-white h-[48px] justify-between border-b border-gray-200 px-[16px] py-[10px] shadow-sm"}`}
        >
          {isMobile && (
            <IconButtonComponent
              imgWidth={16}
              imgHeight={20}
              iconSrc="/icons/left-arrow.svg"
              onClick={handleCloseMobileMultiTextDrawer}
            />
          )}
          <Typography Tag="p" classes="w-full ml-8 flex items-center flex-shrink-0 font-600 text-16 text-fnp-500">
            {constants.CUSTOMIZE_PRODUCT}
          </Typography>
        </div>

        <div className="h-[calc(100%-150px)] overflow-y-auto scroll-smooth p-20">
          {personalizedOptions?.map((option, index) => (
            <TextPersonalization
              key={index}
              productId={productId}
              indexNumber={index}
              option={option}
              multiText={true}
              isInvalid={invalidFields[option.fieldName]}
              onInputChange={(value) => onInputChange(value, index, option?.fieldName)}
              setTextsAddedSuccessfully={setTextsAddedSuccessfully}
              setMultiTextPersonalization={setMultiTextPersonalization}
            />
          ))}
        </div>

        <div className="bg-white fixed bottom-0 flex w-full flex-col items-center gap-[10px] border-t border-gray-200 px-[20px] py-[16px]">
          <ButtonComponent
            classes="text-base flex justify-center w-[360px]"
            buttonVariant="filled"
            label={constants.SAVE_AND_CONTINUE}
            onClick={onContinueMultiText}
            isRemoveExtraPadding={true}
          />
        </div>

        <div className={`bottom-100 ${toastStyles}`}>
          <Toast
            variant="info"
            isToast={isToastVisible}
            header={constants.CONTINUE_WITH_DETAILS}
            duration={3000}
            onToastChange={() => setIsToastVisible(false)}
          />
        </div>
      </Drawer>
    </div>
  );
};

MultiTextPersonlization.propTypes = {
  isMobile: PropTypes.bool,
  personalizedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      fieldName: PropTypes.string.isRequired,
      contentType: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  imageWithText: PropTypes.bool,
  isUploadIncomplete: PropTypes.bool,
  setTextEntered: PropTypes.func,
  setMultiImageDrawer: PropTypes.func,
  setPreviewImage: PropTypes.func,
  productId: PropTypes.string,
};

export default MultiTextPersonlization;
